import { formatListData } from 'commons-ui';
import {
	HomePageLayout,
	HomePageWidgetsCategory,
	ILiveGamesBuckets,
	IVersionSupportedLiveGames
} from 'commons/models/homePage.model';
import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
import { GAME_NAME } from 'commons/superApp/models/games';
import { GAMES, superAppLiveGamesBuckets } from './constants';
import { IHomePageLayoutOrder, IVersionBasedHomePageLayout, IViewHomePageLayoutOrder } from './model';

export const HOME_LAYOUT_ZK_VALUES = {
	serviceName: 'user.service',
	keyName: 'VersionSupportedliveGamesV2'
};

export const formatDataAsOrginalLayout = (createdData: IViewHomePageLayoutOrder): IHomePageLayoutOrder => {
	if (GAMES.includes(createdData.placeholderFor.toUpperCase())) {
		return {
			placeholderFor: HomePageWidgetsCategory.GAMES,
			gameName: createdData.placeholderFor as GAME_NAME,
			widgetType: createdData.widgetType,
			numOfCols: createdData.numOfCols,
			isSpaceWidgetRequiredBelow: createdData.isSpaceWidgetRequiredBelow,
			index: createdData.index
		};
	}

	return {
		placeholderFor: createdData.placeholderFor as HomePageWidgetsCategory,
		isSpaceWidgetRequiredBelow: createdData.isSpaceWidgetRequiredBelow,
		index: createdData.index
	};
};

export const sortLayouts = (layouts: IVersionBasedHomePageLayout[]): void => {
	layouts.sort((a: IVersionBasedHomePageLayout, b: IVersionBasedHomePageLayout) => {
		if (a.minAppVersion !== b.minAppVersion) {
			return a.minAppVersion - b.minAppVersion;
		}
		if (a.maxAppVersion !== b.maxAppVersion) {
			return a.maxAppVersion - b.maxAppVersion;
		}
		return a.bucket.localeCompare(b.bucket);
	});
};

export const getResponseFormat = (data: Array<any>, rowCount: number) => {
	const dataList = !_isNil(data) ? data : [];
	const layouts = dataList.map((versionLayouts: any) => {
		return {
			...versionLayouts,
			layoutItemCount: versionLayouts.layout.length
		};
	});
	sortLayouts(layouts);
	return {
		rowCount: !_isNil(rowCount) ? rowCount : 0,
		data: formatListData(layouts)
	};
};

export const getHomePageLayoutFormattedData = (
	minVersion: number,
	maxVersion: number,
	bucket: keyof ILiveGamesBuckets,
	homePageLayout: HomePageLayout[]
): IVersionBasedHomePageLayout => {
	return {
		minAppVersion: minVersion,
		maxAppVersion: maxVersion,
		bucket,
		layout: homePageLayout.map((item: HomePageLayout, index: number) => {
			return {
				...item,
				index: index + 1,
				...(_isNil(item.isSpaceWidgetRequiredBelow) && { isSpaceWidgetRequiredBelow: false })
			};
		})
	};
};

export const getFormattedLayout = (layout: IHomePageLayoutOrder): IViewHomePageLayoutOrder => {
	if (layout.placeholderFor === HomePageWidgetsCategory.GAMES) {
		return {
			placeholderFor: layout.gameName as GAME_NAME,
			widgetType: layout.widgetType,
			numOfCols: layout.numOfCols,
			isSpaceWidgetRequiredBelow: layout.isSpaceWidgetRequiredBelow,
			index: layout.index
		};
	}

	return {
		placeholderFor: layout.placeholderFor,
		isSpaceWidgetRequiredBelow: layout.isSpaceWidgetRequiredBelow,
		index: layout.index
	};
};

export const setLayoutsInLocal = (layouts: IVersionBasedHomePageLayout[]): void => {
	window.localStorage.setItem(HOME_LAYOUT_ZK_VALUES.keyName, JSON.stringify(layouts));
};

export const removeLayoutsFromLocal = (): void => {
	window.localStorage.removeItem(HOME_LAYOUT_ZK_VALUES.keyName);
};

export const getLayoutsFromLocal = (): IVersionBasedHomePageLayout[] => {
	const layoutsString: string | null = window.localStorage.getItem(HOME_LAYOUT_ZK_VALUES.keyName);
	return layoutsString != null ? JSON.parse(layoutsString) : [];
};

export const getParentRowLayoutIndex = (rowData: IVersionBasedHomePageLayout, layouts: IVersionBasedHomePageLayout[]): number => {
	const index: number = layouts.findIndex(
		(layout: any) =>
			layout.minAppVersion === rowData.minAppVersion &&
			layout.maxAppVersion === rowData.maxAppVersion &&
			layout.bucket === rowData.bucket
	);
	return index;
};

export const isParentLayoutExist = (rowData: IVersionBasedHomePageLayout): boolean => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const layoutsIndex: number = getParentRowLayoutIndex(rowData, layouts);
	return layoutsIndex >= 0;
};

export const getLayoutFromLocal = (parentRowData: IVersionBasedHomePageLayout): IHomePageLayoutOrder[] => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const layoutsIndex: number = getParentRowLayoutIndex(parentRowData, layouts);
	if (layoutsIndex >= 0) {
		const { layout }: { layout: IHomePageLayoutOrder[] } = layouts[layoutsIndex];
		return layout;
	}
	return [];
};

const getLayouts = (data: IVersionBasedHomePageLayout): IVersionBasedHomePageLayout => {
	return {
		minAppVersion: data.minAppVersion,
		maxAppVersion: data.maxAppVersion,
		bucket: data.bucket,
		layout: data.layout
	};
};

export const addNewVersionLayout = (layout: IVersionBasedHomePageLayout): void => {
	const versionSupportedLayouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	setLayoutsInLocal([...versionSupportedLayouts, layout]);
};

export const updateLayoutsInLocal = (rowData: IVersionBasedHomePageLayout, data: IVersionBasedHomePageLayout) => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const layoutsIndex: number = getParentRowLayoutIndex(rowData, layouts);
	if (layoutsIndex < 0) return;

	if (!_isNil(data)) {
		layouts.splice(layoutsIndex, 1, getLayouts(data));
	} else {
		layouts.splice(layoutsIndex, 1);
	}
	setLayoutsInLocal(layouts);
};

export const updateLayoutInLocal = (
	parentRowData: IVersionBasedHomePageLayout,
	existingData: IHomePageLayoutOrder,
	data?: IHomePageLayoutOrder
): void => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const parentLayoutIndex: number = getParentRowLayoutIndex(parentRowData, layouts);
	if (parentLayoutIndex >= 0) {
		const { layout }: { layout: IHomePageLayoutOrder[] } = layouts[parentLayoutIndex];
		const index: number = layout.findIndex((item: IHomePageLayoutOrder) => _isEqual(item, existingData));
		if (index < 0) return;

		if (!_isNil(data)) {
			layout.splice(index, 1, data);
		} else {
			layout.splice(index, 1);
		}

		setLayoutsInLocal(layouts);
	}
};

export const deleteLayoutInLocal = (parentRowData: IVersionBasedHomePageLayout, existingData: IHomePageLayoutOrder): void => {
	updateLayoutInLocal(parentRowData, existingData);
};

export const deleteLayoutsInLocal = (rowData: IVersionBasedHomePageLayout): void => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const layoutsIndex: number = getParentRowLayoutIndex(rowData, layouts);
	if (layoutsIndex >= 0) {
		layouts.splice(layoutsIndex, 1);
		setLayoutsInLocal(layouts);
	}
};

export const pushNewLayoutInLocal = (rowData: IVersionBasedHomePageLayout, data: IHomePageLayoutOrder): void => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const index: number = getParentRowLayoutIndex(rowData, layouts);
	if (index >= 0) {
		const { layout } = layouts[index];
		layout.push(data);
		setLayoutsInLocal(layouts);
	}
};

export const sortLayout = (layout: Array<IHomePageLayoutOrder | IViewHomePageLayoutOrder>): void => {
	layout.sort((a, b) => a.index - b.index);
};

export const getBucketLayout = (currVersionLayout: IVersionBasedHomePageLayout): HomePageLayout[] => {
	sortLayout(currVersionLayout.layout);
	return currVersionLayout.layout.map((layout: IHomePageLayoutOrder) => {
		if (layout.placeholderFor === HomePageWidgetsCategory.GAMES) {
			return {
				widgetType: layout.widgetType,
				numOfCols: layout.numOfCols,
				placeholderFor: layout.placeholderFor,
				gameName: layout.gameName,
				...(layout.isSpaceWidgetRequiredBelow === true && { isSpaceWidgetRequiredBelow: layout.isSpaceWidgetRequiredBelow })
			};
		}

		return {
			placeholderFor: layout.placeholderFor,
			...(layout.isSpaceWidgetRequiredBelow === true && { isSpaceWidgetRequiredBelow: layout.isSpaceWidgetRequiredBelow })
		};
	});
};

export const createVersionSupportedConfigFromLocal = (): IVersionSupportedLiveGames => {
	const layouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();
	const versionSupportedConfig = {} as IVersionSupportedLiveGames;
	sortLayouts(layouts);
	layouts.forEach((versionLayout: IVersionBasedHomePageLayout) => {
		const versionRange = `${versionLayout.minAppVersion}-${versionLayout.maxAppVersion}`;
		if (!versionSupportedConfig[versionRange]) {
			versionSupportedConfig[versionRange] = {} as ILiveGamesBuckets;
		}
		versionSupportedConfig[versionRange][versionLayout.bucket] = getBucketLayout(versionLayout);
	});
	return versionSupportedConfig;
};

const validateInput = (value: any, message: string) => {
	if (!value) {
		throw new Error(message);
	}
};

export const readBuckets = (versionLayouts: ILiveGamesBuckets, versionRange: string): Array<keyof ILiveGamesBuckets> => {
	const buckets: Array<keyof ILiveGamesBuckets> = Object.keys(versionLayouts).map((bucket) => {
		validateInput(
			versionLayouts[bucket as keyof ILiveGamesBuckets]!.length > 0,
			`Empty layout found for bucket: ${bucket} in versionRange: ${versionRange}`
		);
		return bucket as keyof ILiveGamesBuckets;
	});
	return buckets;
};

export const validateAllBuckets = (layouts: IVersionSupportedLiveGames): void => {
	validateInput(layouts, `Empty layouts`);
	Object.keys(layouts).forEach((versionRange) => {
		const buckets: Array<keyof ILiveGamesBuckets> = readBuckets(layouts[versionRange], versionRange);
		validateInput(
			buckets.length === superAppLiveGamesBuckets.length,
			`buckets missing for version: ${versionRange} -> ${JSON.stringify(superAppLiveGamesBuckets)}`
		);
		superAppLiveGamesBuckets.forEach((bucket) => {
			validateInput(
				buckets.includes(bucket as keyof ILiveGamesBuckets),
				`Bucket: ${bucket} not present in ${JSON.stringify(buckets)}`
			);
		});
	});
};
